import { ArrowUpRight, Check, Eye, Leaf } from "lucide-react";
import { Reveal, Overline } from "./Reveal";
import { SITE } from "../config/site";
import { track } from "../lib/analytics";
import { useContent, useBookingUrl } from "../context/ContentContext";

const SERVICES = [
  {
    id: "terrain-truth",
    icon: Leaf,
    badge: "A Gentle Starting Point",
    title: "Terrain & Truth Consultation",
    duration: "60 minutes",
    price: "$88",
    features: [
      "One-on-one virtual session with Staci",
      "Whole-person look at your current health",
      "Space for your questions, unhurried",
      "Practical next steps to take home",
    ],
  },
  {
    id: "full-90",
    icon: Eye,
    badge: "The Deeper Dive",
    title: "Full 90-Minute Consultation",
    duration: "90 minutes · includes iridology",
    price: "$234",
    features: [
      "Extended one-on-one virtual session",
      "Iridology assessment included",
      "A fuller picture of your constitution",
      "Personalized guidance to take home",
    ],
    featured: true,
  },
];

export const Consultations = () => {
  const terrainBlurb = useContent(
    "consult_terrain_blurb",
    "A focused, one-on-one conversation about where your health stands and where it could go."
  );
  const fullBlurb = useContent(
    "consult_full_blurb",
    "A comprehensive session with time to go deeper, including a personal iridology assessment."
  );
  const consultNote = useContent(
    "consult_note",
    "Service descriptions are being lovingly refined. Final details will be confirmed with Staci shortly."
  );
  const bookingUrl = useBookingUrl();
  const services = SERVICES.map((s) =>
    s.id === "terrain-truth" ? { ...s, blurb: terrainBlurb } : { ...s, blurb: fullBlurb }
  );

  return (
    <section id="consultations" data-testid="consultation-services-section" className="py-24 sm:py-32">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <Reveal className="mx-auto max-w-2xl text-center">
          <Overline>Consultations</Overline>
          <h2 className="font-serif text-3xl leading-tight text-forest sm:text-4xl lg:text-5xl">
            Choose the Support That Fits You
          </h2>
          <p className="mt-6 text-lg font-light leading-relaxed text-charcoal/75">
            Two calm, unhurried ways to begin. Booking is handled simply and securely through
            Staci's online scheduler.
          </p>
        </Reveal>
        <div className="mx-auto mt-16 grid max-w-4xl grid-cols-1 gap-8 md:grid-cols-2">
          {services.map((s, i) => (
            <Reveal key={s.id} delay={i * 0.12}>
              <article
                data-testid={`service-card-${s.id}`}
                className={`group relative flex h-full flex-col rounded-[2rem] border p-9 transition-all duration-500 hover:-translate-y-1.5 ${
                  s.featured
                    ? "border-forest bg-forest text-ivory shadow-[0_30px_60px_rgba(23,63,53,0.28)]"
                    : "border-forest/15 bg-white/60 shadow-[0_16px_40px_rgba(23,63,53,0.08)] hover:shadow-[0_28px_55px_rgba(23,63,53,0.16)]"
                }`}
              >
                <span
                  className={`inline-flex w-fit items-center rounded-full px-4 py-1.5 text-[11px] font-semibold uppercase tracking-[0.2em] ${
                    s.featured ? "bg-gold text-forest-deep" : "bg-sage text-forest"
                  }`}
                >
                  {s.badge}
                </span>
                <s.icon className={`mt-8 h-7 w-7 ${s.featured ? "text-gold-soft" : "text-gold"}`} />
                <h3 className={`mt-4 font-serif text-2xl ${s.featured ? "text-ivory" : "text-forest"}`}>
                  {s.title}
                </h3>
                <p className={`mt-1 text-sm ${s.featured ? "text-ivory/70" : "text-taupe"}`}>{s.duration}</p>
                <p className={`mt-4 text-5xl font-serif ${s.featured ? "text-gold-soft" : "text-forest"}`}>
                  {s.price}
                </p>
                <p className={`mt-4 leading-relaxed ${s.featured ? "text-ivory/80" : "text-charcoal/70"}`}>
                  {s.blurb}
                </p>
                <ul className="mt-6 flex flex-col gap-3">
                  {s.features.map((f) => (
                    <li key={f} className="flex items-start gap-3">
                      <Check className={`mt-0.5 h-4 w-4 shrink-0 ${s.featured ? "text-gold-soft" : "text-gold"}`} />
                      <span className={`text-sm leading-relaxed ${s.featured ? "text-ivory/85" : "text-charcoal/75"}`}>
                        {f}
                      </span>
                    </li>
                  ))}
                </ul>
                <a
                  href={bookingUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`service-book-button-${s.id}`}
                  onClick={() => track("booking_click", { location: `service-${s.id}` })}
                  className={`mt-9 inline-flex items-center justify-center gap-2 rounded-full px-7 py-3.5 text-sm font-semibold transition-all duration-300 group/btn ${
                    s.featured
                      ? "bg-ivory text-forest hover:bg-gold-soft"
                      : "bg-forest text-ivory hover:bg-forest-deep"
                  }`}
                >
                  Book This Session
                  <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover/btn:translate-x-0.5 group-hover/btn:-translate-y-0.5" />
                </a>
              </article>
            </Reveal>
          ))}
        </div>
        <Reveal delay={0.2}>
          <p className="mx-auto mt-10 max-w-xl text-center text-sm text-taupe">{consultNote}</p>
        </Reveal>
      </div>
    </section>
  );
};
